// @flow

import * as R from 'ramda';

import { AceService, ApolloService, HttpService } from '~/services';
import {
  parseBool,
  renameKeys,
  getRouteNavigationParamsWithFormattedDate,
} from '~/helpers';
import { ELEVATE_TOKENS, ELEVATE_TOKENS_MUTATION, CITIZEN_LIST } from '~/gql';

const getDepositInfo = (
  locale,
  {
    DepositBalance,
    AvailableBalance,
    CityScore,
    CityScorePercentage,
    CityScorePercentage2,
    NewCityScore,
    NewCityScorePercentage,
    NewCityScorePercentage2,
    IsDepositEnabled,
    EstimatedUsd,
  },
): DepositInfo => {
  const isEnLocale = locale === 'en';

  return {
    city: {
      scoreValue: parseFloat(CityScore),
      scorePtc: parseFloat(
        isEnLocale ? CityScorePercentage : CityScorePercentage2,
      ),
      nextScoreValue: parseFloat(NewCityScore),
      nextScorePtc: parseFloat(
        isEnLocale ? NewCityScorePercentage : NewCityScorePercentage2,
      ),
      __typename: 'ScoreValues',
    },
    depositBalance: parseFloat(DepositBalance),
    availableBalance: parseFloat(AvailableBalance),
    isDepositEnabled: parseBool(IsDepositEnabled),
    estimatedUsd: parseFloat(EstimatedUsd),
    __typename: 'DepositInfo',
  };
};

const loanPlanMutationOnce = R.once(
  async (
    _, // eslint-disable-line no-unused-vars
    { values }: LoanPlanInputMutation,
  ): Promise<MessagePayload> => {
    const params = renameKeys({ planId: 'PlanId', userName: 'Username' })(
      R.pick(['planId', 'idToken', 'userName'], values),
    );

    const authTokens = await AceService.getAuthTokens();

    HttpService.setAuthorizationToken(values.accessToken);

    try {
      await HttpService.client.post('/MyWealth/Loan/LoanApplication', params);
      await ApolloService.client.mutate({
        mutation: ELEVATE_TOKENS_MUTATION,
        refetchQueries: [
          {
            query: ELEVATE_TOKENS,
          },
        ],
        variables: {
          values: null,
        },
      });
      HttpService.setAuthorizationToken(authTokens.accessToken);

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    } catch (error) {
      HttpService.setAuthorizationToken(authTokens.accessToken);

      return {
        code: 0,
        message: error.message,
        __typename: 'MessagePayload',
      };
    }
  },
);

export const resolvers = {
  Query: {
    depositHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?DepositHistory> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        TrxId: 'trxId',
        Amount: 'amount',
        AmountInMbtc: 'amountInMbtc',
        AmountInUsd: 'amountInUsd',
        Status: 'status',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          trxId: ['TrxIdFrom', 'TrxIdTo'],
          amount: ['AmountFrom', 'AmountTo'],
          status: 'Status',
        },
        R.invertObj(columnsMapping),
      )(props);

      const {
        data: { DepositList, PageCount = 1 },
      } = await HttpService.client.get('/MyWealth/Deposit/GetDepositTrxList', {
        params,
      });

      const items: DepositHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({ amount: parseFloat }),
          R.evolve({ amountInMbtc: parseFloat }),
          R.evolve({ amountInUsd: parseFloat }),
          R.assoc('__typename', 'DepositHistoryItem'),
        ),
        DepositList,
      );

      const depositHistory = {
        data: items,
        pages: PageCount,
        __typename: 'DepositHistory',
      };

      cache.writeData({ data: { depositHistory }, variables: props });

      return depositHistory;
    },
    depositPlansList: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      // $FlowIssue
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<?(DepositPlansListItem[])> => {
      const {
        data: { DepositPlanLine },
      } = await HttpService.client.get('/MyWealth/Deposit/GetDepositPlan');

      const depositPlansList: DepositPlansListItem[] = R.map(
        R.pipe(
          renameKeys({
            Id: 'id',
            DepositPlan: 'depositPlan',
            Quantity: 'quantityLeft',
            EstimatedUsd: 'estimatedUsd',
          }),
          R.evolve({
            quantityLeft: parseFloat,
            depositPlan: parseFloat,
            estimatedUsd: parseFloat,
          }),
          R.assoc('__typename', 'DepositPlansListItem'),
        ),
        DepositPlanLine,
      );

      cache.writeData({ data: { depositPlansList } });

      return depositPlansList;
    },
    depositInfo: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { planId, locale }: DepositInfoQueryInput,
      { cache }: ApolloCache,
    ): Promise<?DepositInfo> => {
      const {
        data: { DepositDetails },
      } = await HttpService.client.get(
        '/MyWealth/Deposit/GetDepositInitiateScoring',
        { params: { PlanId: planId } },
      );

      const depositInfo: DepositInfo = getDepositInfo(locale, DepositDetails);

      cache.writeData({ data: { depositInfo }, variables: { planId } });

      return depositInfo;
    },
    citizenList: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { page = 1 }: CitizenListQueryInput,
      { cache }: ApolloCache,
    ): Promise<?CitizenList> => {
      const {
        data: { CitizenList, IsLoanEnabled, PageNum = 1, PageCount },
      } = await HttpService.client.get('/MyWealth/Loan/GetCitizenName', {
        params: {
          PageNum: page,
          PageSize: 1000,
        },
      });

      const items: CitizenListItem[] = R.map(
        R.pipe(
          renameKeys({ Username: 'userName' }),
          R.assoc('__typename', 'CitizenListItem'),
          (item) => ({ ...item, id: item.userName }),
        ),
        CitizenList,
      );

      const citizenList = {
        items,
        isLoanEnabled: parseBool(IsLoanEnabled),
        pageNum: PageNum,
        pageCount: PageCount,
        __typename: 'CitizenList',
      };

      cache.writeQuery({
        query: CITIZEN_LIST,
        data: { citizenList },
        variables: { page },
      });

      return citizenList;
    },
    loanPlanList: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { userName }: LoanPlanListQueryInput,
      { cache }: ApolloCache,
    ): Promise<?(LoanPlanListItem[])> => {
      const {
        data: { LoanPlanLine },
      } = await HttpService.client.get('/MyWealth/Loan/GetBtcVoucherPlan', {
        params: { Username: userName },
      });

      const loanPlanList: LoanPlanListItem[] = R.map(
        R.pipe(
          renameKeys({
            Id: 'id',
            Plan: 'amount',
            EstimatedUsd: 'estimatedUsd',
          }),
          R.evolve({
            plan: parseFloat,
            estimatedUsd: parseFloat,
            amount: parseFloat,
          }),
          R.assoc('__typename', 'LoanPlanListItem'),
        ),
        LoanPlanLine,
      );

      cache.writeData({ data: { loanPlanList }, variables: { userName } });

      return loanPlanList;
    },
    loanHistory: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?LoanList> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        TrxId: 'trxId',
        Amount: 'amount',
        Status: 'status',
        Username: 'userName',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          trxId: ['TrxIdFrom', 'TrxIdTo'],
          amount: ['AmountFrom', 'AmountTo'],
          status: 'Status',
          userName: 'Username',
        },
        R.invertObj(columnsMapping),
      )(props);

      const {
        data: { LoanList, PageCount = 1 },
      } = await HttpService.client.get('/MyWealth/Loan/GetLoanTrxList', {
        params,
      });

      const items: LoanHistoryItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({ amount: parseFloat }),
          R.assoc('__typename', 'LoanHistoryItem'),
        ),
        LoanList,
      );

      const loanHistory = {
        data: items,
        pages: PageCount,
        __typename: 'LoanHistory',
      };

      cache.writeData({ data: { loanHistory }, variables: props });

      return loanHistory;
    },
  },
  Mutation: {
    depositPlanMutation: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { planId, locale }: DepositPlanInputMutation,
      { cache }: ApolloCache,
    ): Promise<MessagePayload> => {
      const {
        data: { DepositDetails },
      } = await HttpService.client.post(
        '/MyWealth/Deposit/DepositApplication',
        {
          PlanId: planId,
        },
      );

      const depositInfo: DepositInfo = getDepositInfo(locale, DepositDetails);

      // Update cache
      cache.writeData({ data: { depositInfo }, variables: { planId } });

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    },
    loanPlanMutation: loanPlanMutationOnce,
  },
};
